import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormattedMessage, FormModal, useIntl } from '@platform/cj-platform-navigation'
import { DataGrid } from '@cjdev-internal/visual-stack-x/DataGrid'
import { FlexPage } from '@cjdev-internal/visual-stack-x/FlexPage'
import { ActionModal } from '@cjdev-internal/visual-stack-x/ActionModal'
import { useToast } from '@cjdev-internal/visual-stack-x/Toast'
import { Drawer } from '@cjdev-internal/visual-stack-x/Drawer'
import { Button } from '@cjdev-internal/visual-stack-x/Button'
import { Row } from '@cjdev-internal/visual-stack-x/Row'
import { Text } from '@cjdev-internal/visual-stack-x/Text'
import { ExpandingInputButton } from '@cjdev-internal/visual-stack-x/ExpandingInputButton'
import useApi from '../../PlatformApi'
import {
  DEFAULT_COMPANY_NAME,
  SETUP_COMPANY_NAME,
  STATUS_DEACTIVATED,
  STATUS_SETUP,
  STATUS_TEMPORARILY_DEACTIVATED
} from '../../utils/constants'
import { AddAccountModal } from './AddAccountModal'
import styles from './ManageAccounts.module.css'
import './ManageAccounts.css'

export const ManageAccounts = ({ user }) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [mount, show, dismiss] = useToast()
  const [latestToastId, setLatestToastId] = useState(null)
  const [isOpen, setIsOpen] = useState(true)
  const [openModal, setOpenModal] = useState(false)
  const [selectedCompanies, setSelectedCompanies] = useState([])
  const [selectedCompany, setSelectedCompany] = useState([0, ''])
  const [gridApi, setGridApi] = useState(null)
  const [multiSelect, setMultiSelect] = useState(false)
  const confirmationMessage = intl
    .formatMessage({
      defaultMessage: 'Please confirm that you want to remove this account(s):'
    })
    .concat(
      multiSelect ? selectedCompanies.map((company) => company.CID).join(', ') : selectedCompany[0]
    )

  const successMessage = multiSelect ? (
    selectedCompanies
      .map((company) => company.CID + ' - ' + company.Name ?? DEFAULT_COMPANY_NAME)
      .join(', ')
      .concat(
        '. ',
        intl.formatMessage({
          defaultMessage: 'It may take up to a minute to be removed from the platform.'
        })
      )
  ) : (
    <FormattedMessage
      defaultMessage="Account {id} - {name} removed. It may take up to a minute to be removed from the platform."
      values={{ id: selectedCompany[0], name: selectedCompany[1] || DEFAULT_COMPANY_NAME }}
    />
  )

  const closeModalConfirm = () => {
    setOpenModal(!openModal)
  }

  const closeModal = () => {
    setIsOpen(false)
    navigate(-1)
  }

  const showSuccessMessage = () => {
    if (latestToastId) dismiss(latestToastId)
    const newToastId = show({
      message: successMessage,
      contentStyle: { textAlign: 'center', minWidth: '300px' },
      type: 'success',
      duration: 5000
    })
    setLatestToastId(newToastId)
  }

  const showErrorMessage = ({ message }) => {
    if (latestToastId) dismiss(latestToastId)
    const newToastId = show({
      message: message,
      contentStyle: { textAlign: 'center', minWidth: '300px' },
      type: 'warning',
      duration: 5000
    })
    setLatestToastId(newToastId)
  }

  return (
    <FormModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      onSubmit={() => console.log('form submitted')}
      submitText={<FormattedMessage defaultMessage="Save" />}
      title={<FormattedMessage defaultMessage="Manage Accounts" />}
    >
      <div className={styles.manageAccountsModalContent}>
        <ManageAccountsHeader user={user} />
        <ManageAccountsBody
          data={user?.companies}
          setOpenModal={setOpenModal}
          setSelectedCompanies={setSelectedCompanies}
          intl={intl}
          setSelectedCompany={setSelectedCompany}
          setGridApi={setGridApi}
          setMultiSelect={setMultiSelect}
        />
      </div>
      {mount}
      <ConfirmationModal
        user={user}
        openModal={openModal}
        closeModalConfirm={closeModalConfirm}
        selectedCompanies={selectedCompanies}
        showSuccessMessage={showSuccessMessage}
        showErrorMessage={showErrorMessage}
        confirmationMessage={confirmationMessage}
        companyId={selectedCompany[0]}
        multiSelect={multiSelect}
      />
      <Drawer isOpen={selectedCompanies.length > 0}>
        <Row padding="large" align="center" justify="space-between" expand gap="medium">
          <Row gap="large" align="center">
            <Text color="white" size={14} weight="medium">
              <FormattedMessage
                defaultMessage="{number} selected"
                values={{ number: selectedCompanies.length }}
              />
            </Text>
            <Text
              color="light"
              onClick={() => gridApi.api.deselectAll()}
              className={styles.unSelectAllText}
              size={13}
              weight="medium"
            >
              <FormattedMessage defaultMessage="Unselect All" />
            </Text>
          </Row>
          <Button
            type="critical"
            onClick={() => {
              setMultiSelect(true)
              setOpenModal(true)
            }}
          >
            <FormattedMessage defaultMessage="Remove" />
          </Button>
        </Row>
      </Drawer>
    </FormModal>
  )
}

export const ManageAccountsHeader = ({ user }) => {
  return (
    <div className={styles.manageAccountsHeader}>
      <div className={styles.manageAccountsTitle}>
        <span className={styles.manageAccountsTitleSpan}>
          <FormattedMessage defaultMessage="Accounts" />
        </span>
        <AddAccountModal user={user} />
      </div>
    </div>
  )
}

export const ManageAccountsBody = ({
  data,
  setOpenModal,
  setSelectedCompanies,
  intl,
  setSelectedCompany,
  setGridApi,
  setMultiSelect
}) => {
  const resolveStatus = ({ option }) => {
    let status = option.status
    if (option.type === 'advertiser') {
      if (option.status === STATUS_TEMPORARILY_DEACTIVATED) {
        status = STATUS_DEACTIVATED
      }
    }
    return status
  }
  const options = data
    .reduce(function (filtered, option) {
      if (
        option.type === 'advertiser' &&
        (option.status === STATUS_TEMPORARILY_DEACTIVATED || option.status === STATUS_DEACTIVATED)
      ) {
        return filtered
      }
      const newOption = {
        CID: option.id,
        Name:
          option.name ||
          (option?.status === STATUS_SETUP ? SETUP_COMPANY_NAME : DEFAULT_COMPANY_NAME),
        Role: option.companyRole,
        Status: resolveStatus({ option })
      }
      filtered.push(newOption)
      return filtered
    }, [])
    .sort((a, b) => a.Name.toUpperCase().localeCompare(b.Name.toUpperCase()))

  const rowActions = () => [
    {
      label: intl.formatMessage({ defaultMessage: 'Remove' }),
      icon: 'delete',
      onClick: (e) => {
        setSelectedCompany([e.rowData.CID, e.rowData.Name])
        setOpenModal(true)
        setMultiSelect(false)
      }
    }
  ]

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      sortable: true,
      resizable: true,
      filter: true,
      flex: 1,
      minWidth: 200
    }
  }, [])

  const topPanel = ({ api }) => {
    return (
      <Row justify="space-between" align="center" gap={'medium'} expand={true}>
        <Row align="center" expand={false}>
          <Text weight={'medium'}>
            <FormattedMessage
              defaultMessage="{accounts} Accounts"
              values={{ accounts: data.length }}
            />
          </Text>
        </Row>
        <Row align="center" expand={false}>
          <ExpandingInputButton onChange={(v) => api.setQuickFilter(v)} />
        </Row>
      </Row>
    )
  }

  const dataGridProps = {
    columnDefs: [
      { field: 'Name', headerName: intl.formatMessage({ defaultMessage: 'Name' }) },
      {
        field: 'CID',
        headerName: intl.formatMessage({ defaultMessage: 'CID' })
      },
      {
        field: 'Role',
        headerName: intl.formatMessage({ defaultMessage: 'Role' })
      },
      {
        field: 'Status',
        headerName: intl.formatMessage({ defaultMessage: 'Status' })
      }
    ],
    getRowId: (item) => item.CID.toString(),
    rowData: options,
    rowActions: rowActions,
    pagination: false,
    defaultColDef: defaultColDef,
    onGridReady: (p) => {
      p.columnApi.autoSizeAllColumns(true)
      setGridApi(p)
    },
    rowSelection: 'multiple',
    onSelectionChanged: (p) => {
      setSelectedCompanies(p.api.getSelectedRows())
    },
    topPanel: topPanel,
    headerBoxProps: { justify: 'space-between' }
  }

  return (
    <FlexPage heightAdjust="32px" className={styles.manageAccountsTable}>
      <DataGrid {...dataGridProps} />
    </FlexPage>
  )
}

const ConfirmationModal = ({
  openModal,
  closeModalConfirm,
  user,
  selectedCompanies,
  showSuccessMessage,
  showErrorMessage,
  confirmationMessage,
  companyId,
  multiSelect
}) => {
  const intl = useIntl()
  const api = useApi()

  function removeAccount(companiesIds) {
    if (user?.currentCompany.type === 'advertiser')
      return api.deleteLinkAdvertisers(user.userId, companiesIds)
    else if (user?.currentCompany.type === 'publisher')
      return api.deleteLinkPublishers(user.userId, companiesIds)
  }

  const onConfirmRemoveAccount = () => {
    removeAccount(multiSelect ? selectedCompanies.map((company) => company.CID).join() : companyId)
      .then((response) => {
        if (!response.ok) {
          throw new Error(intl.formatMessage({ defaultMessage: 'Something went wrong' }))
        }
      })
      .then(() => showSuccessMessage())
      .catch((err) =>
        showErrorMessage({
          message: err.message
        })
      )
      .finally(closeModalConfirm)
  }

  return (
    <ActionModal
      isOpen={openModal}
      headerText={<FormattedMessage defaultMessage="Remove Selected Account(s)?" />}
      onRequestClose={closeModalConfirm}
      onSubmit={onConfirmRemoveAccount}
      onCloseIconClick={closeModalConfirm}
      type={'default'}
      body={confirmationMessage}
      submitButtonText={<FormattedMessage defaultMessage="Remove" />}
      criticalSubmit={true}
    />
  )
}

export function useOutsideClick(cb, deps = []) {
  const ref = useRef(null)
  useEffect(() => {
    function handleOutsideClick(e) {
      if (!ref.current?.contains(e.target)) cb(e)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
    // eslint-disable-next-line
  }, [ref, cb, ...deps])

  return ref
}
